import { Component, inject, signal } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { LegalAcceptance } from '../../services/user.service';
import { LEGAL_VERSION } from '../../models/user.model';

type AuthMode = 'login' | 'signup';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  private fb          = inject(FormBuilder);
  private authService = inject(AuthService);
  private route       = inject(ActivatedRoute);

  private get returnUrl(): string | undefined {
    return this.route.snapshot.queryParamMap.get('returnUrl') ?? undefined;
  }

  mode: AuthMode = 'login';
  loading        = false;
  errorMsg       = '';
  /** Checkbox state — gates both Google and email signup CTAs. */
  readonly acceptedLegal = signal(false);

  form = this.fb.nonNullable.group({
    email:    ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]],
  });

  get email()    { return this.form.controls.email; }
  get password() { return this.form.controls.password; }

  toggleMode(): void {
    this.mode     = this.mode === 'login' ? 'signup' : 'login';
    this.errorMsg = '';
    this.acceptedLegal.set(false);
    this.form.reset();
  }

  toggleLegal(): void {
    this.acceptedLegal.update(v => !v);
  }

  /** Audit payload built only on the signup path. */
  private legalPayload(): LegalAcceptance | undefined {
    if (this.mode !== 'signup') return undefined;
    return { legalAccepted: true, legalVersion: LEGAL_VERSION };
  }

  async loginWithGoogle(): Promise<void> {
    if (this.mode === 'signup' && !this.acceptedLegal()) return;
    this.errorMsg = '';
    this.loading  = true;
    try {
      await this.authService.signInWithGoogle(this.returnUrl, this.legalPayload());
    } catch (e: unknown) {
      this.errorMsg = this.mapError(this.errorCode(e));
    } finally {
      this.loading = false;
    }
  }

  async submitForm(): Promise<void> {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    if (this.mode === 'signup' && !this.acceptedLegal()) return;
    this.errorMsg = '';
    this.loading  = true;
    const { email, password } = this.form.getRawValue();
    try {
      if (this.mode === 'login') {
        await this.authService.signInWithEmail(email, password, this.returnUrl);
      } else {
        await this.authService.signUpWithEmail(email, password, this.legalPayload());
      }
    } catch (e: unknown) {
      this.errorMsg = this.mapError(this.errorCode(e));
    } finally {
      this.loading = false;
    }
  }

  private errorCode(e: unknown): string {
    const code = (e as { code?: string })?.code;
    if (code) return code;
    if (e instanceof Error && e.message) return e.message;
    return 'unknown';
  }

  private mapError(code: string): string {
    if (code.startsWith('APP_CHECK_UNAVAILABLE:')) {
      const host = code.slice('APP_CHECK_UNAVAILABLE:'.length) || 'tu-dominio';
      return (
        `App Check no pudo validar este sitio (${host}). Comprueba: ` +
        `(1) Google Cloud → reCAPTCHA Enterprise → site key → dominios incluye "${host}"; ` +
        `(2) Firebase → Authentication → Settings → Authorized domains → "${host}"; ` +
        `(3) Authentication → reCAPTCHA → plataforma Web en "None"; ` +
        `(4) Firebase → App Check → app web registrada con la misma site key.`
      );
    }

    const map: Record<string, string> = {
      'auth/user-not-found':       'No existe ninguna cuenta con ese email.',
      'auth/wrong-password':       'Contraseña incorrecta.',
      'auth/invalid-credential':   'Email o contraseña incorrectos.',
      'auth/email-already-in-use': 'Este email ya está registrado.',
      'auth/weak-password':        'La contraseña debe tener al menos 6 caracteres.',
      'auth/invalid-email':        'El formato del email no es válido.',
      'auth/popup-closed-by-user': 'Ventana de Google cerrada. Inténtalo de nuevo.',
      'auth/network-request-failed': 'Error de red. Comprueba tu conexión.',
      'auth/captcha-check-failed': 'Verificación anti-bot fallida. Revisa reCAPTCHA / App Check.',
      'auth/internal-error':       'Error interno de autenticación (suele ser reCAPTCHA).',
      'APP_CHECK_UNAVAILABLE':
        'App Check no está listo. Revisa la consola (F12) y la configuración de dominios.',
      'appCheck/throttled':
        'App Check bloqueado 24h por un error 403 anterior. Borra datos del sitio en el navegador (o espera 24h) y corrige el debug token / dominios reCAPTCHA.',
      'FIRESTORE_AFTER_AUTH':
        'Sesión iniciada pero no se pudo guardar el perfil (Firestore / App Check). Comprueba reglas y App Check.',
    };
    return map[code] ?? 'Ha ocurrido un error. Inténtalo de nuevo.';
  }
}
